// src/components/cards/ConsumoCard.js

/**
 * Card de captura de consumo por marca con integración API.
 *
 * Responsabilidades:
 * - Mostrar la marca y la cantidad de modelos disponibles.
 * - Permitir seleccionar una combinación Familia/Año (modelo) mediante
 *   un dropdown con buscador.
 * - Permitir seleccionar un catálogo asociado al modelo seleccionado,
 *   también con buscador.
 * - Capturar el valor de consumo (km/l) y notificar al padre mediante
 *   `onSaveConsumo` que ahora conecta con la API.
 * - Disparar la apertura del resumen de consumos por marca con
 *   `onViewConsumos`.
 * - Mostrar estado de loading cuando se envían datos a la API.
 *
 * Props principales:
 * - marca: nombre de la marca (FORD, GWM, etc.).
 * - modelos: arreglo [{ id, familia, anoModelo, label, catalogos: [...] }].
 * - isOpen / onToggleOpen: controlan la apertura del combo de catálogos.
 * - isModelOpen / onToggleModelOpen: controlan la apertura del combo
 *   de modelos (familia/año).
 * - onSaveConsumo(marca, detalle, consumo): callback para registrar
 *   el consumo de la combinación seleccionada via API.
 * - onViewConsumos(): abre el modal de resumen de consumos de la marca.
 * - hasConsumos: indica si ya existen consumos registrados para la marca.
 * - isSubmitting: 🆕 indica si se está enviando datos a la API.
 */

import React, { useState, useMemo, useEffect } from 'react';

const ConsumoCard = ({
  marca,
  modelos,
  isOpen,             // abierto/cerrado del combo de CATÁLOGO (por marca)
  onToggleOpen,       // toggler de CATÁLOGO
  onSaveConsumo,
  isModelOpen,        // abierto/cerrado del combo de MODELO/AÑO (por marca)
  onToggleModelOpen,  // toggler de MODELO/AÑO
  onViewConsumos,
  hasConsumos,
  isSubmitting = false, // Estado de carga para API
  resetTrigger,
}) => {
  const [selectedModelId, setSelectedModelId] = useState('');
  const [selectedCatalogCode, setSelectedCatalogCode] = useState('');
  const [consumo, setConsumo] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [modelSearchTerm, setModelSearchTerm] = useState('');
  const [isLocalSubmitting, setIsLocalSubmitting] = useState(false);
  useEffect(() => {
    // Reset completo de selección + input + buscadores
    setSelectedModelId('');
    setSelectedCatalogCode('');
    setConsumo('');
    setSearchTerm('');
    setModelSearchTerm('');

    // Cierra dropdowns (son controlados por el padre)
    if (onToggleOpen) onToggleOpen(false);
    if (onToggleModelOpen) onToggleModelOpen(false);
  }, [resetTrigger]);


  // Filtro de modelos (Familia/Año)
  const filteredModels = useMemo(() => {
    if (!modelos) return [];
    const q = modelSearchTerm.toLowerCase();
    if (!q) return modelos;
    return modelos.filter((m) =>
      m.label.toLowerCase().includes(q) ||
      String(m.anoModelo || '').toLowerCase().includes(q)
    );
  }, [modelos, modelSearchTerm]);

  const selectedModel = useMemo(
    () => modelos?.find((m) => m.id === selectedModelId) || null,
    [modelos, selectedModelId]
  );

  const catalogsForModel = selectedModel?.catalogos || [];

  // Filtro de catálogos
  const filteredCatalogs = useMemo(() => {
    const q = searchTerm.toLowerCase();
    if (!q) return catalogsForModel;
    return catalogsForModel.filter((c) =>
      (c.label || '').toLowerCase().includes(q) ||
      (c.code || '').toLowerCase().includes(q)
    );
  }, [catalogsForModel, searchTerm]);

  // Manejo mejorado del submit con loading
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedModel || !selectedCatalogCode || !consumo || isSubmitting || isLocalSubmitting) {
      return;
    }

    const valor = parseFloat(consumo.replace(',', '.'));
    if (Number.isNaN(valor)) return;

    const selectedCatalog = catalogsForModel.find(
      (c) => c.code === selectedCatalogCode
    );

    if (onSaveConsumo) {
      try {
        setIsLocalSubmitting(true);

        await onSaveConsumo(
          marca,
          {
            // Info de modelo / familia
            modelo: selectedModel.familia,
            anoModelo: selectedModel.anoModelo,
            modeloLabel: selectedModel.label,

            // IDs si existen
            familiaId: selectedModel.familiaId ?? null,
            modeloAnoId: selectedModel.modeloAnoId ?? null,

            // Info de catálogo
            catalogo: selectedCatalog?.code,
            catalogoLabel: selectedCatalog?.label,

            // Id de catálogo si existe
            catalogoId: selectedCatalog?.id ?? null,
          },
          valor
        );

        // Solo limpiar el form si la operación fue exitosa
        setConsumo('');

      } catch (error) {
        console.error('Error en ConsumoCard:', error);
        // El error ya se maneja en el componente padre
      } finally {
        setIsLocalSubmitting(false);
      }
    }
  };

  const handleSelectCatalog = (catalogoCode) => {
    if (isSubmitting || isLocalSubmitting) return;

    setSelectedCatalogCode(catalogoCode);
    if (onToggleOpen) onToggleOpen(false);
    setSearchTerm('');
  };

  const handleSelectModel = (modelId) => {
    if (isSubmitting || isLocalSubmitting) return;

    setSelectedModelId(modelId);
    if (onToggleModelOpen) onToggleModelOpen(false); // cerrar menú de modelo
    setModelSearchTerm('');
    setSelectedCatalogCode('');
    // cerrar también catálogo, por si estaba abierto
    if (onToggleOpen) onToggleOpen(false);
  };

  // Determinar si está en estado de carga
  const isLoading = isSubmitting || isLocalSubmitting;

  return (
    <div className={`bg-white rounded-xl shadow-sm border border-gray-200 p-3 sm:p-4 flex flex-col gap-3 sm:gap-4 transition-opacity relative ${isLoading ? 'opacity-75' : ''}`}>
      <div className="flex items-center justify-between">
        <h3 className="text-base sm:text-lg font-semibold text-gray-900">
          {marca}
        </h3>
        <div className="flex items-center gap-2">
          {/* Indicador de carga */}
          {isLoading && (
            <div className="w-4 h-4 border-2 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
          )}
          <span className="text-xs text-gray-500">
            {modelos?.length || 0} modelos
          </span>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="flex flex-col gap-3">
        {/* MODELO / AÑO con buscador */}
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-gray-700">
            Familia / Año
          </label>

          <div className="relative">
            <button
              type="button"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm flex items-center justify-between gap-2 bg-white focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-orange-400 disabled:bg-gray-100 disabled:text-gray-400"
              onClick={() => {
                if (!modelos?.length || isLoading) return;
                if (onToggleModelOpen) onToggleModelOpen(!isModelOpen);
                if (onToggleOpen) onToggleOpen(false);
              }}
              disabled={!modelos?.length || isLoading}
            >
              <span
                className={
                  selectedModel ? 'text-gray-900' : 'text-gray-400'
                }
              >
                {selectedModel?.label || 'Selecciona familia / año'}
              </span>
              <svg
                className={`w-4 h-4 text-gray-500 transition-transform ${isModelOpen ? 'rotate-180' : ''
                  }`}
                viewBox="0 0 20 20"
                fill="none"
              >
                <path
                  d="M5 7.5L10 12.5L15 7.5"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>

            {isModelOpen && modelos?.length > 0 && !isLoading && (
              <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-56 overflow-y-auto">
                {/* buscador de modelo/año */}
                <div className="p-2 border-b border-gray-200 bg-white sticky top-0 z-10">
                  <input
                    type="text"
                    placeholder="Buscar familia o año..."
                    className="w-full px-2 py-1 text-xs border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-orange-400"
                    value={modelSearchTerm}
                    onChange={(e) => setModelSearchTerm(e.target.value)}
                    autoFocus
                  />
                </div>

                <ul className="py-1 text-sm">
                  {filteredModels.length === 0 && (
                    <li className="px-3 py-2 text-xs text-gray-400">
                      Sin resultados
                    </li>
                  )}

                  {filteredModels.map((m) => (
                    <li
                      key={m.id}
                      className={`px-3 py-2 cursor-pointer hover:bg-orange-50 ${m.id === selectedModelId ? 'bg-orange-50 font-medium' : ''
                        }`}
                      onClick={() => handleSelectModel(m.id)}
                    >
                      {m.label}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>

        {/* CATÁLOGO */}
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-gray-700">
            Catálogo
          </label>

          <div className="relative">
            <button
              type="button"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm flex items-center justify-between gap-2 bg-white focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-orange-400 disabled:bg-gray-100 disabled:text-gray-400"
              onClick={() => {
                if (!catalogsForModel.length || isLoading) return;
                if (onToggleOpen) onToggleOpen(!isOpen);
                if (onToggleModelOpen) onToggleModelOpen(false);
              }}
              disabled={!catalogsForModel.length || isLoading}
            >
              <span
                className={
                  selectedCatalogCode ? 'text-gray-900' : 'text-gray-400'
                }
              >
                {selectedCatalogCode
                  ? catalogsForModel.find((c) => c.code === selectedCatalogCode)?.label
                  : 'Selecciona un catálogo'}
              </span>
              <svg
                className={`w-4 h-4 text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''
                  }`}
                viewBox="0 0 20 20"
                fill="none"
              >
                <path
                  d="M5 7.5L10 12.5L15 7.5"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>

            {isOpen && catalogsForModel.length > 0 && !isLoading && (
              <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-56 overflow-y-auto">
                <div className="p-2 border-b border-gray-200 bg-white sticky top-0 z-10">
                  <input
                    type="text"
                    placeholder="Buscar por catálogo..."
                    className="w-full px-2 py-1 text-xs border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-orange-400"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    autoFocus
                  />
                </div>

                <ul className="py-1 text-sm">
                  {filteredCatalogs.length === 0 && (
                    <li className="px-3 py-2 text-xs text-gray-400">
                      Sin resultados
                    </li>
                  )}

                  {filteredCatalogs.map((catalogo) => (
                    <li
                      key={catalogo.code}
                      className={`px-3 py-2 cursor-pointer hover:bg-orange-50 ${catalogo.code === selectedCatalogCode
                          ? 'bg-orange-50 font-medium'
                          : ''
                        }`}
                      onClick={() => handleSelectCatalog(catalogo.code)}
                    >
                      {catalogo.label}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>

        {/* CONSUMO */}
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-gray-700">
            Consumo (km/l)
          </label>
          <input
            type="number"
            step="0.1"
            min="0"
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-orange-400 disabled:bg-gray-100"
            placeholder="Ej. 14.5"
            value={consumo}
            onChange={(e) => setConsumo(e.target.value)}
            disabled={isLoading}
          />
        </div>

        {/* Botones */}
        <div className="flex flex-col gap-2 mt-1">
          <button
            type="submit"
            className="inline-flex items-center justify-center px-3 py-2.5 sm:py-2 text-xs font-medium rounded-lg bg-orange-500 text-white hover:bg-orange-600 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
            disabled={!selectedModel || !selectedCatalogCode || !consumo || isLoading}
          >
            {/* Indicador de carga en el botón */}
            {isLoading ? (
              <>
                <div className="w-3 h-3 mr-2 border border-white border-t-transparent rounded-full animate-spin"></div>
                Guardando...
              </>
            ) : (
              'Guardar consumo'
            )}
          </button>

          <button
            type="button"
            className="text-xs font-medium text-orange-600 hover:text-orange-700 hover:underline transition-colors disabled:text-gray-400 disabled:cursor-not-allowed"
            onClick={() => onViewConsumos && onViewConsumos()}
            disabled={isLoading}
          >
            {hasConsumos ? 'Ver consumos registrados' : 'Ver resumen de consumos'}
          </button>
        </div>
      </form>

      {/* Indicador visual cuando está cargando */}
      {isLoading && (
        <div className="absolute inset-0 bg-white bg-opacity-50 rounded-xl flex items-center justify-center">
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <div className="w-4 h-4 border-2 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
            Procesando...
          </div>
        </div>
      )}
    </div>
  );
};

export default ConsumoCard;